import { isApiError } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Dialog, DialogContent, EmptyState, ErrorState, Icon, Skeleton, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { CaseFormDialog } from "./regression-case-form";
import { formOf, isPending, type RegressionCase } from "./regression-model";
import { RunCard } from "./regression-run-card";
import { useLatestRun, useRegressionCases, useRegressionMutations } from "./use-regression";

const R = "engage.workflows.regression";

/** What a case must do, as short badges. */
function Expectations({ row }: { row: RegressionCase }) {
  const { t } = useTranslation();
  const form = formOf(row);
  const customer = [form.tier, form.status, ...form.attributes.split("\n")].filter((part) => part.length > 0).join(", ");
  const count = form.messages.split("\n").length;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
      <span className="text-fg-muted">{t(`${R}.list.messages`, { count })}</span>
      {customer.length > 0 ? <span className="text-fg-muted">{t(`${R}.list.customer`, { details: customer })}</span> : null}
      {form.path.length > 0 ? <Badge tone="neutral">{t(`${R}.list.path`, { path: form.path })}</Badge> : null}
      {form.handover !== "any" ? <Badge tone="neutral">{t(form.handover === "yes" ? `${R}.list.handoverYes` : `${R}.list.handoverNo`)}</Badge> : null}
      {form.contains.length > 0 ? <Badge tone="neutral">{t(`${R}.list.contains`, { words: form.contains.split("\n").join(", ") })}</Badge> : null}
    </div>
  );
}

interface PanelProps {
  workflowId: string;
  canManage: boolean;
}

/** The workflow's saved test cases: add, edit, delete and run them three times; the last run is shown below. */
export function RegressionCasesPanel({ workflowId, canManage }: PanelProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const cases = useRegressionCases(workflowId);
  const latest = useLatestRun(workflowId);
  const mutations = useRegressionMutations(workflowId);
  const [editing, setEditing] = useState<RegressionCase | "new" | null>(null);
  const [deleting, setDeleting] = useState<RegressionCase | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const items = cases.data?.items ?? [];
  const running = isPending(latest.data) || mutations.run.isPending;
  const full = cases.data !== undefined && items.length >= cases.data.limit;

  const save = (body: Parameters<typeof mutations.create.mutate>[0]) => {
    const done = { onSuccess: () => { setEditing(null); setFormError(null); toast.success(t(`${R}.saved`)); }, onError: (error: unknown) => { setFormError(isApiError(error) && error.status === 409 ? t(`${R}.limit`, { count: cases.data?.limit ?? 0 }) : message(error)); } };
    if (editing !== null && editing !== "new") mutations.update.mutate({ id: editing.id, body }, done);
    else mutations.create.mutate(body, done);
  };

  if (cases.isError) return <ErrorState title={t(`${R}.loadFailed`)} onRetry={() => void cases.refetch()} retryLabel={t("common.retry")} />;
  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>{t(`${R}.title`)}</CardTitle>
          {canManage ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" disabled={full} onClick={() => { setFormError(null); setEditing("new"); }}><Icon name="plus" aria-hidden />{t(`${R}.add`)}</Button>
              <Button disabled={items.length === 0 || running} loading={running} onClick={() => { mutations.run.mutate(undefined, { onSuccess: () => { toast.info(t(`${R}.runStarted`)); }, onError: (error) => { toast.error(message(error)); } }); }}><Icon name="play" aria-hidden />{t(`${R}.run`)}</Button>
            </div>
          ) : null}
        </CardHeader>
        <CardBody className="space-y-3">
          <p className="text-sm text-fg-muted">{t(`${R}.intro`)}</p>
          {cases.isPending ? (
            <div className="space-y-2" aria-busy="true">{[0, 1, 2].map((key) => <Skeleton key={key} className="h-14 rounded-card" />)}</div>
          ) : items.length === 0 ? (
            <EmptyState
              icon="list-checks" title={t(`${R}.empty.title`)} description={t(`${R}.empty.body`)}
              action={canManage ? <Button onClick={() => { setFormError(null); setEditing("new"); }}><Icon name="plus" aria-hidden />{t(`${R}.add`)}</Button> : undefined}
            />
          ) : (
            <ul className="divide-y divide-line rounded-card border border-line" data-testid="regression-cases">
              {items.map((row) => (
                <li key={row.id} className="flex items-start justify-between gap-3 p-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{row.name}</p>
                    <Expectations row={row} />
                  </div>
                  {canManage ? (
                    <div className="flex shrink-0 gap-1">
                      <Button size="sm" variant="ghost" aria-label={t(`${R}.list.edit`, { name: row.name })} onClick={() => { setFormError(null); setEditing(row); }}><Icon name="pencil" aria-hidden /></Button>
                      <Button size="sm" variant="ghost" aria-label={t(`${R}.list.delete`, { name: row.name })} onClick={() => { setDeleting(row); }}><Icon name="trash" aria-hidden /></Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
      {items.length > 0 ? <RunCard run={latest.isError ? null : latest.data} cases={items} /> : null}
      {editing !== null ? (
        <CaseFormDialog
          editing={editing === "new" ? null : editing} busy={mutations.create.isPending || mutations.update.isPending} error={formError}
          onSubmit={save} onClose={() => { setEditing(null); }}
        />
      ) : null}
      {deleting !== null ? (
        <Dialog open onOpenChange={(open) => { if (!open) setDeleting(null); }}>
          <DialogContent
            title={t(`${R}.list.deleteTitle`)} description={t(`${R}.list.deleteBody`, { name: deleting.name })} closeLabel={t("common.close")}
            footer={
              <>
                <Button variant="secondary" onClick={() => { setDeleting(null); }}>{t("common.cancel")}</Button>
                <Button variant="danger" loading={mutations.remove.isPending} onClick={() => { mutations.remove.mutate(deleting.id, { onSuccess: () => { setDeleting(null); toast.success(t(`${R}.deleted`)); }, onError: (error) => { toast.error(message(error)); } }); }}>{t(`${R}.list.confirmDelete`)}</Button>
              </>
            }
          />
        </Dialog>
      ) : null}
    </div>
  );
}
